import React, { useEffect } from 'react';
import { createPortal } from 'react-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPrint, faTimes } from '@fortawesome/free-solid-svg-icons';
import logo from '../../assets/logo192.png';
import '../../styles/LeaveRequestPrint.css';

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

const showDate = (value) => {
  if (!value) return '—';
  const text = String(value);
  const match = text.match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (match && text.length <= 10) {
    return `${match[3]} ${MONTHS[Number(match[2]) - 1]} ${match[1]}`;
  }
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return '—';
  const day = String(parsed.getDate()).padStart(2, '0');
  return `${day} ${MONTHS[parsed.getMonth()]} ${parsed.getFullYear()}`;
};

const periodLabel = (start) => {
  const year = Number(String(start || '').slice(0, 4));
  if (!year) return '—';
  return `${year}-${year + 1}`;
};

const plainNumber = (value) => {
  const amount = Number(value);
  if (!Number.isFinite(amount)) return '—';
  const rounded = Math.round(amount * 100) / 100;
  const whole = Math.abs(rounded - Math.round(rounded)) < 0.001;
  return rounded.toLocaleString(undefined, {
    minimumFractionDigits: whole ? 0 : 2,
    maximumFractionDigits: whole ? 0 : 2,
  });
};

const le = (value) => {
  const text = plainNumber(value);
  return text === '—' ? text : `Le ${text}`;
};

const formula = (voucher, multiplier) => (
  `Le ${plainNumber(voucher.base_pay)} x ${plainNumber(voucher.allowance_entitled_days)} x ${plainNumber(multiplier)} / ${plainNumber(voucher.daily_rate_divisor)}`
);

const LeavePaymentVoucherPrint = ({ voucher, onClose }) => {
  useEffect(() => {
    const handleKey = (event) => {
      if (event.key === 'Escape') onClose();
    };
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    window.addEventListener('keydown', handleKey);
    return () => {
      document.body.style.overflow = previousOverflow;
      window.removeEventListener('keydown', handleKey);
    };
  }, [onClose]);

  const handlePrint = () => {
    document.body.classList.add('leave-request-printing');
    document.body.style.overflow = 'visible';
    const cleanup = () => {
      document.body.classList.remove('leave-request-printing');
      document.body.style.overflow = 'hidden';
      window.removeEventListener('afterprint', cleanup);
    };
    window.addEventListener('afterprint', cleanup);
    window.print();
  };

  const name = voucher.employee_name || 'the employee';

  return createPortal(
    <div className="leave-print-overlay">
      <div className="leave-print-backdrop no-print" onClick={onClose} />
      <div className="leave-print-sheet" role="dialog" aria-modal="true" aria-labelledby="voucher-print-title">
        <div className="leave-print-actions no-print">
          <button type="button" className="leave-print-button" onClick={handlePrint}>
            <FontAwesomeIcon icon={faPrint} /> Print
          </button>
          <button type="button" className="leave-print-close" onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} /> Close
          </button>
        </div>

        <header className="leave-print-hospital">
          <img src={logo} alt="Shuman Hospital" className="leave-print-logo" />
          <div>
            <h2>Shuman Hospital</h2>
            <p>15-17 Pultney Street, Freetown</p>
            <p>Tel: (+232) 090 33 00 00</p>
          </div>
        </header>

        <h1 id="voucher-print-title">Leave pay due to {name}</h1>
        <p className="leave-print-voucher-no">Voucher {voucher.voucher_id}</p>

        <table className="leave-print-table">
          <tbody>
            <tr>
              <th>Date</th>
              <td>{showDate(voucher.issued_at)}</td>
              <th>Occupation</th>
              <td>{voucher.occupation || '—'}</td>
            </tr>
            <tr>
              <th>Basic salary</th>
              <td>{le(voucher.base_pay)}</td>
              <th>Department</th>
              <td>{voucher.department || '—'}</td>
            </tr>
            <tr>
              <th>Leave year</th>
              <td>{periodLabel(voucher.leave_year_start)}</td>
              <th>Duration</th>
              <td>1 year</td>
            </tr>
          </tbody>
        </table>

        <h2>Entitlements</h2>
        <table className="leave-print-table">
          <tbody>
            <tr>
              <th>1. Leave allowance</th>
              <td>{formula(voucher, 1)}</td>
              <td>{le(voucher.standard_amount)}</td>
            </tr>
            {voucher.forfeited_amount != null && (
              <tr>
                <th>2. Forfeited leave allowance</th>
                <td>{formula(voucher, voucher.forfeited_leave_multiplier)}</td>
                <td>{le(voucher.forfeited_amount)}</td>
              </tr>
            )}
            <tr>
              <th>Total</th>
              <td />
              <td><strong>{le(voucher.total_amount)}</strong></td>
            </tr>
          </tbody>
        </table>

        <p className="leave-print-statement">
          I, {name}, will take this voucher to the accounts office to be paid.
          End of service is not on this voucher.
        </p>

        <h2>Signatures</h2>
        <div className="leave-print-approvals leave-print-voucher-signs">
          <section>
            <h3>Paid by</h3>
            <p><span>Name</span> Accounts office</p>
            <p><span>Date</span> —</p>
            <div className="leave-print-sign">Signature</div>
          </section>
          <section>
            <h3>Prepared by</h3>
            <p><span>Name</span> {voucher.prepared_by_name || '—'}</p>
            <p><span>Title</span> {voucher.prepared_by_title || '—'}</p>
            <p><span>Date</span> {showDate(voucher.issued_at)}</p>
            <div className="leave-print-sign">Signature</div>
          </section>
          <section>
            <h3>Received by</h3>
            <p><span>Name</span> {name}</p>
            <p><span>Date</span> —</p>
            <div className="leave-print-sign">Signature</div>
          </section>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default LeavePaymentVoucherPrint;
